import {
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { vietnamesePhoneNumberValidator } from '@shared/utils/phone-validator.util';
import { emailValidatorFn } from '@shared/utils/email-validator.util';

// Vendor Form Data Interface
export interface VendorFormData {
  vendorCode: string;
  vendorName: string;
  vendorEmail: string;
  vendorPhone: string;
  vendorAddress: string;
  description?: string;
  country?: string;
  taxNumber?: string;
  vendorType?: string;
  billingCurrency?: string;
  paymentTerm?: string;
  paymentMethod?: string;
  bankAccounts?: Array<{
    id?: number;
    bankAccountName?: string;
    bankAccountNumber?: string;
    bankName?: string;
    swiftCode?: string;
  }>;
  category?: string;
  riskLevel?: string;
  vendorPostingGroup?: string;
  generalBusinessPostingGroup?: string;
  vatBusinessPostingGroup?: string;
  invoiceDiscCode?: string;
}

// Vendor Type Options
export const VENDOR_TYPE_OPTIONS = [
  { label: 'vendor.form.vendor_type.company', value: 'COMPANY' },
  { label: 'vendor.form.vendor_type.individual', value: 'INDIVIDUAL' },
  { label: 'vendor.form.vendor_type.partnership', value: 'PARTNERSHIP' },
];

// Payment Term Options
export const PAYMENT_TERM_OPTIONS = [
  { label: 'vendor.form.payment_term.net30', value: 'NET30' },
  { label: 'vendor.form.payment_term.net60', value: 'NET60' },
  { label: 'vendor.form.payment_term.prepaid', value: 'PREPAID' },
  { label: 'vendor.form.payment_term.cod', value: 'COD' },
];

// Payment Method Options
export const PAYMENT_METHOD_OPTIONS = [
  { label: 'vendor.form.payment_method.bank_transfer', value: 'BANK_TRANSFER' },
  { label: 'vendor.form.payment_method.cod', value: 'COD' },
  { label: 'vendor.form.payment_method.cash', value: 'CASH' },
];

// Currency Options
export const CURRENCY_OPTIONS = [
  { label: 'VND', value: 'VND' },
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
];

// Risk Level Options
export const RISK_LEVEL_OPTIONS = [
  { label: 'vendor.form.risk_level.low', value: 'LOW' },
  { label: 'vendor.form.risk_level.medium', value: 'MEDIUM' },
  { label: 'vendor.form.risk_level.high', value: 'HIGH' },
];

// Vendor Posting Group Options
export const VENDOR_POSTING_GROUP_OPTIONS = [
  {
    label: 'vendor.form.vendor_posting_group.domestic_short_term_service',
    value: 'DOMESTIC_SHORT_TERM_SERVICE',
  },
  {
    label: 'vendor.form.vendor_posting_group.domestic_short_term_goods',
    value: 'DOMESTIC_SHORT_TERM_GOODS',
  },
  {
    label: 'vendor.form.vendor_posting_group.foreign_short_term_goods',
    value: 'FOREIGN_SHORT_TERM_GOODS',
  },
  {
    label: 'vendor.form.vendor_posting_group.domestic_long_term',
    value: 'DOMESTIC_LONG_TERM',
  },
  {
    label: 'vendor.form.vendor_posting_group.shop_deposit',
    value: 'SHOP_DEPOSIT',
  },
];

// Form fields configuration organized by sections
export const VENDOR_FORM_FIELDS: FormField[] = [
  // ============================================================================
  // SECTION 1: BASIC INFORMATION
  // ============================================================================
  {
    controlName: 'vendorCode',
    label: 'vendor.form.fields.vendor_code',
    type: 'text',
    section: 'vendor.form.sections.basic_information',
    required: true,
    placeholder: 'vendor.form.placeholders.enter_vendor_code',
    maxLength: 50,
  },
  {
    controlName: 'vendorName',
    label: 'vendor.form.fields.vendor_name',
    type: 'text',
    section: 'vendor.form.sections.basic_information',
    required: true,
    placeholder: 'vendor.form.placeholders.enter_vendor_name',
    maxLength: 255,
  },
  {
    controlName: 'vendorType',
    label: 'vendor.form.fields.vendor_type',
    type: 'dropdown',
    section: 'vendor.form.sections.basic_information',
    required: false,
    placeholder: 'vendor.form.placeholders.select_vendor_type',
    options: VENDOR_TYPE_OPTIONS,
    showClear: true,
  },
  {
    controlName: 'country',
    label: 'vendor.form.fields.country',
    type: 'dropdown',
    section: 'vendor.form.sections.basic_information',
    required: true,
    placeholder: 'vendor.form.placeholders.select_country',
    options: [], // Will be populated dynamically
    showClear: false,
  },
  {
    controlName: 'category',
    label: 'vendor.form.fields.category',
    type: 'text',
    section: 'vendor.form.sections.basic_information',
    required: false,
    placeholder: 'vendor.form.placeholders.enter_category',
    maxLength: 255,
  },
  {
    controlName: 'riskLevel',
    label: 'vendor.form.fields.risk_level',
    type: 'dropdown',
    section: 'vendor.form.sections.basic_information',
    required: false,
    placeholder: 'vendor.form.placeholders.select_risk_level',
    options: RISK_LEVEL_OPTIONS,
    showClear: true,
  },
  {
    controlName: 'description',
    label: 'vendor.form.fields.description',
    type: 'textarea',
    section: 'vendor.form.sections.basic_information',
    required: false,
    placeholder: 'vendor.form.placeholders.enter_description',
    maxLength: 1000,
    customStyles: {
      minHeight: '80px',
    },
  },

  // ============================================================================
  // SECTION 2: TAX INFORMATION
  // ============================================================================
  {
    controlName: 'taxNumber',
    label: 'vendor.form.fields.tax_number',
    type: 'text',
    section: 'vendor.form.sections.tax_information',
    required: true,
    placeholder: 'vendor.form.placeholders.enter_tax_number',
    maxLength: 50,
  },

  // ============================================================================
  // SECTION 3: CONTACT & ADDRESS INFORMATION
  // ============================================================================
  {
    controlName: 'vendorEmail',
    label: 'vendor.form.fields.vendor_email',
    type: 'text',
    section: 'vendor.form.sections.contact_address',
    required: true,
    placeholder: 'vendor.form.placeholders.enter_vendor_email',
    validators: [emailValidatorFn],
    errorMessages: {
      required: 'vendor.form.validation.vendor_email_required',
      invalidEmail: 'common.validation.invalid_email_format',
    },
  },
  {
    controlName: 'vendorPhone',
    label: 'vendor.form.fields.vendor_phone',
    type: 'text',
    section: 'vendor.form.sections.contact_address',
    required: true,
    placeholder: 'vendor.form.placeholders.enter_vendor_phone',
    maxLength: 20,
    validators: [vietnamesePhoneNumberValidator],
    errorMessages: {
      required: 'vendor.form.validation.vendor_phone_required',
      invalidVietnamesePhone: 'common.validation.invalid_phone_format',
    },
  },
  {
    controlName: 'vendorAddress',
    label: 'vendor.form.fields.vendor_address',
    type: 'text',
    section: 'vendor.form.sections.contact_address',
    required: true,
    placeholder: 'vendor.form.placeholders.enter_vendor_address',
    maxLength: 500,
  },

  // ============================================================================
  // SECTION 4: INVOICING INFORMATION
  // ============================================================================
  {
    controlName: 'billingCurrency',
    label: 'vendor.form.fields.billing_currency',
    type: 'dropdown',
    section: 'vendor.form.sections.invoicing_information',
    required: false,
    placeholder: 'vendor.form.placeholders.select_billing_currency',
    options: CURRENCY_OPTIONS,
    showClear: true,
  },
  {
    controlName: 'vendorPostingGroup',
    label: 'vendor.form.fields.vendor_posting_group',
    type: 'dropdown',
    section: 'vendor.form.sections.invoicing_information',
    required: false,
    placeholder: 'vendor.form.placeholders.select_vendor_posting_group',
    options: VENDOR_POSTING_GROUP_OPTIONS,
    showClear: true,
  },
  {
    controlName: 'generalBusinessPostingGroup',
    label: 'vendor.form.fields.general_business_posting_group',
    type: 'text',
    section: 'vendor.form.sections.invoicing_information',
    required: false,
    placeholder:
      'vendor.form.placeholders.enter_general_business_posting_group',
    maxLength: 50,
  },
  {
    controlName: 'vatBusinessPostingGroup',
    label: 'vendor.form.fields.vat_business_posting_group',
    type: 'text',
    section: 'vendor.form.sections.invoicing_information',
    required: false,
    placeholder: 'vendor.form.placeholders.enter_vat_business_posting_group',
    maxLength: 50,
  },
  {
    controlName: 'invoiceDiscCode',
    label: 'vendor.form.fields.invoice_disc_code',
    type: 'text',
    section: 'vendor.form.sections.invoicing_information',
    required: false,
    placeholder: 'vendor.form.placeholders.enter_invoice_disc_code',
    maxLength: 50,
  },

  // ============================================================================
  // SECTION 5: PAYMENT INFORMATION
  // ============================================================================
  {
    controlName: 'paymentTerm',
    label: 'vendor.form.fields.payment_term',
    type: 'dropdown',
    section: 'vendor.form.sections.payment_information',
    required: false,
    placeholder: 'vendor.form.placeholders.select_payment_term',
    options: PAYMENT_TERM_OPTIONS,
    showClear: true,
  },
  {
    controlName: 'paymentMethod',
    label: 'vendor.form.fields.payment_method',
    type: 'dropdown',
    section: 'vendor.form.sections.payment_information',
    required: false,
    placeholder: 'vendor.form.placeholders.select_payment_method',
    options: PAYMENT_METHOD_OPTIONS,
    showClear: true,
  },

  // ============================================================================
  // SECTION 6: BANK ACCOUNT INFORMATION
  // Note: Bank accounts are handled via TableSection, not form fields
  // SWIFT code has been moved to basic information section
  // ============================================================================
];

// Header actions for add mode
export const VENDOR_ADD_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'common.actions.save',
    action: 'save',
    severity: 'info',
  },
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    severity: 'secondary',
    outlined: true,
  },
];

// Header actions for edit mode
export const VENDOR_EDIT_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'common.actions.save',
    action: 'save',
    severity: 'info',
  },
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    severity: 'secondary',
    outlined: true,
  },
];

// Header actions for view mode
export const VENDOR_VIEW_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'common.actions.back',
    action: 'cancel',
    severity: 'secondary',
    outlined: true,
  },
];
